import { useMemo, type ReactNode } from 'react'
import { mdiCloudDownloadOutline } from '@mdi/js'
import { AnalyticsScreenConstants, catalogVersionIsNewer, countCatalogUpdatesAvailable } from '@buxus/shared'
import { sortCatalogByDisplayName } from './platform/buxus/operatorApi'
import { useOperatorSession } from './platform/buxus/OperatorSessionContext'
import type { OperatorWithVersionDto } from './platform/buxus/types'
import { NavMdiIcon } from './NavTabIcons'
import { EventTypes, trackEvent } from './platform/analytics/analytics'

const DOWNLOAD_SCREEN = AnalyticsScreenConstants.DOWNLOAD_SCREEN

type DownloadListRow = {
  operatorId: string
  name: string
  currentVersion: string
  catalogOp: OperatorWithVersionDto | null
  /** Row built from local cache while the catalog request is in flight. */
  isPlaceholder: boolean
}

type Props = {
  onContinue?: (operatorId?: string) => void
  /** Home route before any operators are downloaded — same UI, different heading copy. */
  homeOnboarding?: boolean
}

/** Status indicator only (not interactive) — matches legacy cloud-download-outline. */
function CloudDownloadOutlineIcon({ variant }: { variant: 'pending' | 'downloading' }) {
  return (
    <span
      className={`app-download-icon-wrap app-download-status-icon app-download-status-icon--${variant}`}
      aria-hidden
    >
      <NavMdiIcon path={mdiCloudDownloadOutline} size={1.375} className="app-download-icon-svg" />
    </span>
  )
}

function CheckCircleOkIcon() {
  return (
    <svg className="app-download-icon-svg" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="#53d769"
        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
      />
    </svg>
  )
}

function DownloadFailedIcon() {
  return (
    <span className="app-download-icon-wrap app-download-status-icon app-download-status-icon--failed">
      <svg className="app-download-icon-svg" viewBox="0 0 24 24" aria-hidden>
        <path
          fill="currentColor"
          d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z"
        />
      </svg>
    </span>
  )
}

function DownloadAllSpinner() {
  return <span className="app-download-btn-spinner" aria-hidden />
}

function CatalogRefreshSpinner() {
  return <span className="app-download-refresh-spinner" aria-hidden />
}

export function DownloadOperatorsScreen({ onContinue, homeOnboarding = false }: Props) {
  const {
    catalog,
    catalogLoading,
    catalogError,
    refreshCatalog,
    persisted,
    downloadedOperators,
    downloadOperator,
    downloadAllOperators,
    clearDownloadedOperators,
    clearDownloadedOperator,
    downloadAllBusy,
    downloadProgressByOperator,
    downloadNotice,
    downloadFailedOperatorIds,
  } = useOperatorSession()

  const anyRowDownloading = useMemo(
    () => Object.keys(downloadProgressByOperator).length > 0,
    [downloadProgressByOperator],
  )

  const catalogSorted = useMemo(() => sortCatalogByDisplayName(catalog), [catalog])
  const catalogListPending = catalog === null && !catalogError
  const catalogAwaitingFirstLoad = catalogListPending && downloadedOperators.length === 0

  const downloadListRows = useMemo((): DownloadListRow[] => {
    if (catalogSorted?.length) {
      return catalogSorted.map((op) => ({
        operatorId: op.operatorId,
        name: op.name,
        currentVersion: op.currentVersion,
        catalogOp: op,
        isPlaceholder: false,
      }))
    }
    if (catalogListPending && downloadedOperators.length > 0) {
      return downloadedOperators.map((e) => ({
        operatorId: e.operatorId,
        name: e.name,
        currentVersion: e.cachedVersion,
        catalogOp: null,
        isPlaceholder: true,
      }))
    }
    return []
  }, [catalogSorted, catalogListPending, downloadedOperators])

  const listIsPlaceholder = downloadListRows.length > 0 && downloadListRows[0]?.isPlaceholder === true
  const catalogRefreshInPlace = catalogLoading && (catalog?.length ?? 0) > 0
  const showCatalogRefreshIndicator = catalogLoading || catalogListPending
  /** Dim for any in-flight catalog fetch (incl. non-blocking reload with cached rows). */
  const listDimmedWhileLoading = showCatalogRefreshIndicator

  /** User-facing copy is unified; `catalogRefreshInPlace` / `catalogListPending` still distinguish reload modes in code. */
  const catalogRefreshStatusLabel = 'Updating operator list…'

  const cachedVersionByOperatorId = useMemo(() => {
    const map: Record<string, string> = {}
    for (const [id, entry] of Object.entries(persisted.entries)) {
      map[id] = entry.cachedVersion
    }
    return map
  }, [persisted.entries])

  /** Same set as nav badge and per-row Download buttons (missing or update-available, with SAS). */
  const catalogDownloadsNeededCount = useMemo(
    () => countCatalogUpdatesAvailable(catalog, cachedVersionByOperatorId),
    [catalog, cachedVersionByOperatorId],
  )

  /** Global busy: block conflicting actions (refresh, clear-all, navigation shortcuts). */
  const downloadActionsLocked =
    catalogLoading ||
    catalogAwaitingFirstLoad ||
    downloadAllBusy ||
    anyRowDownloading

  return (
    <section className="app-download-card">
        <h2 className="app-download-title">{homeOnboarding ? 'Home' : 'Download'}</h2>

        {homeOnboarding ? (
          <p className="app-download-intro">
            To get started, download one or more operators.
          </p>
        ) : null}

        <div className="app-download-actions">
          <div className="app-download-actions-primary">
            <button
              type="button"
              className={`app-download-btn primary${downloadAllBusy ? ' app-download-btn-loading' : ''}`}
              disabled={
                showCatalogRefreshIndicator ||
                downloadAllBusy ||
                anyRowDownloading ||
                catalogDownloadsNeededCount === 0
              }
              aria-busy={downloadAllBusy}
              onClick={() => {
                trackEvent(EventTypes.BUTTON_PRESSED, {
                  screen: DOWNLOAD_SCREEN,
                  button: 'Download All',
                })
                void downloadAllOperators()
              }}
            >
              {downloadAllBusy ? (
                <>
                  <DownloadAllSpinner />
                  Downloading All…
                </>
              ) : (
                'Download All'
              )}
            </button>
            {downloadedOperators.length > 0 && onContinue ? (
              <button
                type="button"
                className="app-download-btn primary"
                disabled={downloadActionsLocked}
                onClick={() => onContinue()}
              >
                Go to Operators
              </button>
            ) : null}
            {showCatalogRefreshIndicator ? (
              <span className="app-download-refresh-status" aria-live="polite">
                <CatalogRefreshSpinner />
                {catalogRefreshStatusLabel}
              </span>
            ) : null}
          </div>
          <div className="app-download-actions-secondary">
            <button
              type="button"
              className="app-download-btn secondary"
              onClick={() => {
                trackEvent(EventTypes.BUTTON_PRESSED, {
                  screen: DOWNLOAD_SCREEN,
                  button: 'Refresh',
                })
                void refreshCatalog({ blocking: true })
              }}
              disabled={
                showCatalogRefreshIndicator || downloadAllBusy || anyRowDownloading
              }
            >
              Refresh List
            </button>
            {downloadedOperators.length > 0 ? (
              <button
                type="button"
                className="app-download-btn secondary"
                onClick={() => {
                  trackEvent(EventTypes.BUTTON_PRESSED, {
                    screen: DOWNLOAD_SCREEN,
                    link: 'Clear all',
                  })
                  void clearDownloadedOperators()
                }}
                disabled={downloadActionsLocked}
              >
                Clear All
              </button>
            ) : null}
          </div>
        </div>

        {catalogError ? <p className="app-download-error">{catalogError}</p> : null}

        {downloadNotice ? (
          <p className="app-download-error" role="status" aria-live="polite">
            {downloadNotice}
          </p>
        ) : null}

        {catalog && catalog.length === 0 && !catalogLoading && !catalogListPending && !catalogError ? (
          <p className="app-download-empty">No operators returned for this account.</p>
        ) : null}

        {downloadListRows.length > 0 ? (
          <ul
            className={[
              'app-download-list',
              listIsPlaceholder ? 'app-download-list--catalog-pending' : '',
              listDimmedWhileLoading ? 'app-download-list--refreshing' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            aria-busy={listDimmedWhileLoading}
            data-catalog-refresh={
              showCatalogRefreshIndicator
                ? catalogRefreshInPlace
                  ? 'in-place'
                  : 'pending'
                : undefined
            }
          >
            {downloadListRows.map((row) => {
              const op = row.catalogOp
              const cached = persisted.entries[row.operatorId]
              const progress = downloadProgressByOperator[row.operatorId]
              const isDownloading = !!progress
              const downloadFailed = !!downloadFailedOperatorIds[row.operatorId]
              const hasCached = !!cached || row.isPlaceholder
              const cachedVersionForCompare = cached?.cachedVersion
              const updateAvailable =
                !row.isPlaceholder &&
                !!cached &&
                !!op &&
                !!cachedVersionForCompare &&
                catalogVersionIsNewer(op.currentVersion, cachedVersionForCompare)

              let statusIcon: ReactNode
              let statusTitle = 'Not downloaded'
              if (row.isPlaceholder) {
                statusIcon = <CheckCircleOkIcon />
                statusTitle = 'Downloaded locally — refreshing operator list'
              } else if (isDownloading) {
                statusIcon = <CloudDownloadOutlineIcon variant="downloading" />
                statusTitle = 'Downloading'
              } else if (downloadFailed) {
                statusIcon = <DownloadFailedIcon />
                statusTitle = 'Download failed — try again later'
              } else if (!hasCached || updateAvailable) {
                statusIcon = <CloudDownloadOutlineIcon variant="pending" />
                statusTitle = updateAvailable ? 'Newer catalog version available' : 'Not downloaded'
              } else {
                statusIcon = <CheckCircleOkIcon />
                statusTitle = 'Downloaded for this catalog version'
              }

              /** Only this row is locked while it downloads; other rows may start in parallel. */
              const rowDownloadLocked =
                row.isPlaceholder || catalogLoading || downloadAllBusy || isDownloading

              return (
                <li
                  key={row.operatorId}
                  className={[
                    'download-operator-row',
                    row.isPlaceholder ? 'download-operator-row--catalog-pending' : '',
                    isDownloading ? 'download-operator-row--downloading' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {progress ? (
                    <>
                      <div className="app-download-row-line">
                        <div className="app-download-row-main app-download-row-main--with-progress">
                          <span className="app-download-name">{row.name}</span>
                          <span className="app-download-meta">
                            v{row.currentVersion}
                            {row.isPlaceholder ? ' · cached' : ''}
                            {updateAvailable && !row.isPlaceholder ? ' · update available' : ''}
                          </span>
                          <span className="app-download-row-progress-count" aria-live="polite">
                            {progress.done}/{progress.total}
                          </span>
                        </div>
                      </div>
                      <div className="app-download-row-progress" aria-hidden>
                        <div
                          className="app-download-row-progress-fill"
                          style={{
                            width: `${Math.min(100, (progress.done / Math.max(1, progress.total)) * 100)}%`,
                          }}
                        />
                      </div>
                    </>
                  ) : (
                    <div className="app-download-row-body">
                      <div className="app-download-row-main">
                        <span className="app-download-name">{row.name}</span>
                        <span className="app-download-meta">
                          v{row.currentVersion}
                          {row.isPlaceholder ? ' · cached' : ''}
                          {updateAvailable && !row.isPlaceholder ? ' · update available' : ''}
                        </span>
                      </div>
                    </div>
                  )}
                  <div className="app-download-status-col" title={statusTitle}>
                    {statusIcon}
                  </div>
                  <div className="app-download-actions-col">
                    {!row.isPlaceholder && (!hasCached || updateAvailable) ? (
                      <button
                        type="button"
                        className="app-download-btn small primary"
                        disabled={rowDownloadLocked}
                        onClick={() => {
                          if (!op) return
                          void downloadOperator(op).then(() => {
                            trackEvent(EventTypes.BUTTON_PRESSED, {
                              screen: DOWNLOAD_SCREEN,
                              button: `${op.name} v${op.currentVersion}`,
                              operator: op.name,
                              version: op.currentVersion,
                            })
                          })
                        }}
                        title="Download operator content for offline use"
                      >
                        {isDownloading ? 'Downloading…' : 'Download'}
                      </button>
                    ) : null}
                    {!row.isPlaceholder && hasCached && !updateAvailable ? (
                      <button
                        type="button"
                        className="app-download-link-action tiny"
                        disabled={catalogLoading || downloadAllBusy || isDownloading}
                        onClick={() => {
                          trackEvent(EventTypes.BUTTON_PRESSED, {
                            screen: DOWNLOAD_SCREEN,
                            button: 'Clear cached',
                            operator: row.name,
                            version: row.currentVersion,
                          })
                          void clearDownloadedOperator(row.operatorId)
                        }}
                      >
                        Clear cached
                      </button>
                    ) : null}
                  </div>
                </li>
              )
            })}
          </ul>
        ) : null}
    </section>
  )
}
